import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { Breadcrumbs } from '../../components/breadcrumbs'
import { Layout } from '../../components/layout'
import { useBasketStore, useContentStore, useCustomerStore, useGlobalStore, usePreBookStore } from '../../store/hooks/useStore'
import { Box, Container, Stack } from '../../vanilla'
import { LetUsKnow } from './partials/let-us-know'
import { YourDelivery } from './partials/your-delivery'
import { BookingSlots } from './partials/booking-slots'
import { TObservablePage } from '../../types/commerce'
import { Seo } from '../../components/seo'
import { usePageDataTracking } from '../../analytics/page-tracking'
import useDeliveryBooking from '../../hooks/use-delivery-booking'
import { Slot} from '../../components/cms/Slot'
import { SlotContentItem } from '../../types/cms'
import { PreBookBanner } from './partials/pre-book-banner'
import { PreBookActiveReservation } from '../pre-book-delivery/partials/pre-book-active-reservation'

const BookDelivery: TObservablePage = () => {
  const { deliveryPostalCode, deliverySlotPostalCode, deliverySlotArray, status } = useBasketStore()
  const { getCustomSitePreferenceById, isCustomSitePreferenceSet, customSitePreferences } = useGlobalStore()
  const { getDeliverySlots, loading } = useDeliveryBooking()
  const { BASHeroes, validBASHeroes, BASFooters, validBASFooters, fetchItemsByContentType } =
    useContentStore()
  const sendPageData = usePageDataTracking()
  const pageType = 'bookaslot'
  const pageCategory = 'Delivery'
  const errorStatusOnDeliverySlots = status === 'error'
  const [ isPreBookEnabled, setPreBookEnabled ] = useState<boolean>(false)
  const [ isPreBookForNewCustomersEnabled, setPreBookForNewCustomersEnabled ] = useState<boolean>(false)
  const { isRegistered } = useCustomerStore()
  const { hasValidPreBookedSlot, prebookedOrder } = usePreBookStore()

  const showPreBookBanner = useMemo(() => {
    return isPreBookEnabled && isRegistered && isPreBookForNewCustomersEnabled && !hasValidPreBookedSlot
  }, [isPreBookEnabled, isRegistered, isPreBookForNewCustomersEnabled, hasValidPreBookedSlot])

  const hasActivePreBooking = useMemo(()=> {
    return isPreBookEnabled && hasValidPreBookedSlot
  }, [isPreBookEnabled, hasValidPreBookedSlot, prebookedOrder ])

  useEffect(() => {
    if (loading) return

    // refresh slots data when postcode changes
    if (deliveryPostalCode || deliverySlotPostalCode !== deliveryPostalCode) {
      getDeliverySlots()
    }
  }, [deliveryPostalCode, deliverySlotPostalCode])

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [])
  
  useEffect(() => {
    if (BASHeroes?.length < 1) {
      fetchItemsByContentType(
        'https://www.iceland.co.uk/slot/book-delivery-hero-slot.json',
        'BASHeroes',
      )
    }
    if (BASFooters?.length < 1) {
      fetchItemsByContentType(
        'https://www.iceland.co.uk/slot/book-delivery-information-slot.json',
        'BASFooters',
      )
    }
  }, [BASHeroes, BASFooters])

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('preBookEnabled')) {
        await getCustomSitePreferenceById('preBookEnabled')
      }
      if (!isCustomSitePreferenceSet('preBookForNewCustomersEnabled')) {
        await getCustomSitePreferenceById('preBookForNewCustomersEnabled')
      }
      setPreBookEnabled(customSitePreferences['preBookEnabled'] as boolean)
      setPreBookForNewCustomersEnabled(customSitePreferences['preBookForNewCustomersEnabled'] as boolean)
    })()
  })

  return (
    <Layout>
      <Seo
        title="Book a Delivery Slot | Iceland Delivery"
        description="Book a delivery slot with Iceland and receive your order at a time that suits you."
      />
      <Container paddingX={['0px', '8px']}>
        <Box paddingX="8px" paddingY="12px">
          <Breadcrumbs breadcrumbs={[{id: 'BOOK_DELIVERY', name: 'Book your delivery'}]} />
        </Box>
        <Stack 
          display='flex'
          gap={deliverySlotArray?.length ? '10px' : ['24px','40px']}
          marginBottom={deliverySlotArray?.length ? '0px' : '40px'}
          >
            {validBASHeroes && (
              <Slot
                data={validBASHeroes[0] as unknown as SlotContentItem}
                key={validBASHeroes[0]?._meta?.deliveryKey}
              />
            )}
          
          {hasActivePreBooking && <PreBookActiveReservation />}
          <YourDelivery />
          <BookingSlots />
          {showPreBookBanner && <PreBookBanner />}
          {deliveryPostalCode && !errorStatusOnDeliverySlots ? 
            <Box 
              paddingY={deliverySlotArray?.length ? ['10px', '28px'] : '0px'}
            >
              <LetUsKnow />
            </Box>
          : null}
          {BASFooters && validBASFooters && !errorStatusOnDeliverySlots && (
            <Box display='block' paddingBottom={deliverySlotArray?.length ? '20px' : '0px'}>
              <Slot
                data={validBASFooters[0] as unknown as SlotContentItem}
                key={validBASFooters[0]?._meta?.deliveryKey}
              />
            </Box>
          )}
        </Stack>
      </Container>
    </Layout>
  )
}

export default observer(BookDelivery)
